/**
 * Client HTTP — le seul chemin entre l'app et la micro-API PHP.
 *
 * Il n'y a aucun client de base de données dans le navigateur : ni URL Neon,
 * ni mot de passe, rien à voler dans le bundle. On envoie un nom d'opération et
 * un jeton d'accès JWT court.
 *
 * La rotation est gérée ici : le jeton d'accès est renouvelé avant expiration
 * (ou après un 401) à partir du jeton de rafraîchissement, une seule requête à
 * la fois — les appels concurrents attendent la même promesse.
 */
const BASE = String(import.meta.env.VITE_API_URL ?? '/api').replace(/\/+$/, '');
const STORAGE_KEY = 'autolink.session';
/** Marge avant expiration : on renouvelle sans attendre le 401. */
const SKEW_MS = 30_000;

export interface SessionUser {
  id: string;
  email: string;
  role: 'driver' | 'fleet_manager' | 'ceo_admin';
}

export interface Session {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: SessionUser;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Stockage de session ─────────────────────────────────────────────────────

let session: Session | null = read();
const listeners = new Set<(s: Session | null) => void>();

function read(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    return s?.access_token && s?.refresh_token ? s : null;
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  return session;
}

export function setSession(next: Session | null) {
  session = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* mode privé : la session reste en mémoire */
  }
  listeners.forEach((fn) => fn(session));
}

export function onSessionChange(fn: (s: Session | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Réponse d'authentification de l'API → session locale. */
export function storeTokens(payload: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: SessionUser;
}) {
  setSession({
    access_token: payload.access_token,
    refresh_token: payload.refresh_token,
    expires_at: Date.now() + payload.expires_in * 1000,
    user: payload.user,
  });
}

// ── Requêtes ────────────────────────────────────────────────────────────────

interface Options {
  /** false pour /auth/login, /auth/register, /auth/refresh */
  auth?: boolean;
  method?: 'GET' | 'POST';
}

async function raw<T>(path: string, body: unknown, token?: string, method: 'GET' | 'POST' = 'POST'): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiError(
      isOffline() ? 'Aucune connexion réseau' : 'Serveur injoignable',
      0,
      'network',
    );
  }

  const payload = (await res.json().catch(() => ({}))) as { data?: T; error?: string; code?: string };
  if (!res.ok) throw new ApiError(payload.error ?? 'Erreur réseau', res.status, payload.code);
  return payload.data as T;
}

let refreshing: Promise<Session> | null = null;

/** Renouvelle le couple de jetons. Un seul appel simultané. */
function refresh(): Promise<Session> {
  if (refreshing) return refreshing;
  const current = session;
  if (!current) return Promise.reject(new ApiError('Session absente', 401, 'unauthenticated'));

  refreshing = raw<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
    user: SessionUser;
  }>('/auth/refresh', { refresh_token: current.refresh_token })
    .then((payload) => {
      storeTokens(payload);
      return session as Session;
    })
    .catch((e) => {
      // Rafraîchissement refusé : la session est morte, on la nettoie.
      setSession(null);
      throw e;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

async function accessToken(): Promise<string | null> {
  if (!session) return null;
  if (session.expires_at - SKEW_MS > Date.now()) return session.access_token;
  try {
    return (await refresh()).access_token;
  } catch {
    return null;
  }
}

export async function request<T = unknown>(path: string, body: unknown = {}, opts: Options = {}): Promise<T> {
  const needsAuth = opts.auth !== false;
  const token = needsAuth ? await accessToken() : undefined;
  if (needsAuth && !token) throw new ApiError('Session expirée, reconnectez-vous', 401, 'unauthenticated');

  try {
    return await raw<T>(path, body, token ?? undefined, opts.method ?? 'POST');
  } catch (e) {
    // 401 malgré un jeton valide en apparence : une tentative de rotation, puis on abandonne.
    if (needsAuth && e instanceof ApiError && e.status === 401) {
      const fresh = await refresh().catch(() => null);
      if (fresh) return raw<T>(path, body, fresh.access_token, opts.method ?? 'POST');
      setSession(null);
    }
    throw e;
  }
}

/** Opérations base de données : `POST /rpc { op, args }`. */
export function rpc<T = unknown>(op: string, args: Record<string, unknown> = {}): Promise<T> {
  return request<T>('/rpc', { op, args });
}

export const isOffline = () => typeof navigator !== 'undefined' && !navigator.onLine;
